import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CheckCircle2, XCircle, FileDown, Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { fmtMoney, fmtDate } from "@/lib/format";

/**
 * Public (no-auth) page where a supplier confirms receipt of a
 * Purchase Order and gives an expected delivery date. Reached from
 * the PO email CTA at `/po-confirm/{supplier_token}`.
 */
export default function PoConfirm() {
  const { token } = useParams();
  const { data: po, isLoading, error, refetch } = useQuery({
    queryKey: ["public-po", token],
    queryFn: () => api.getPublicPo(token),
    retry: false,
  });

  const [name, setName] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [note, setNote] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      api.confirmPublicPo(token, {
        confirmer_name: name.trim(),
        delivery_date: deliveryDate,
        note: note.trim(),
      }),
    onSuccess: () => {
      toast.success("Purchase order confirmed. Thank you!");
      refetch();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Something went wrong"),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center space-y-3" data-testid="po-confirm-invalid">
          <XCircle className="size-10 text-red-500 mx-auto" />
          <h1 className="text-xl font-bold">Link not found</h1>
          <p className="text-sm text-muted-foreground">
            This confirmation link is invalid or has been revoked. Please contact
            KINOS at <a href="mailto:info@kinos.tl" className="text-primary underline">info@kinos.tl</a>.
          </p>
        </div>
      </div>
    );
  }

  const conf = po.supplier_confirmation || null;
  const cancelled = po.status === "cancelled";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="po-confirm-page">
      <header className="bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="size-5 text-primary" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                KINOS — Leading Innovation Technology
              </div>
              <div className="font-semibold text-sm">Purchase order confirmation</div>
            </div>
          </div>
          <a
            href={`${API}/public/po/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-primary text-sm hover:underline"
            data-testid="po-confirm-pdf-link"
          >
            <FileDown className="size-4" /> View PDF
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        {conf?.confirmed_at && (
          <div
            data-testid="po-confirm-done-banner"
            className="rounded-md p-4 border bg-emerald-50 border-emerald-200 text-emerald-900"
          >
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="size-5" />
              This purchase order has been confirmed.
            </div>
            <div className="text-xs mt-1.5 opacity-80">
              By {conf.confirmer_name} on {fmtDate(conf.confirmed_at.slice(0, 10))} —
              delivery expected {fmtDate(conf.delivery_date)}
              {conf.note ? ` — “${conf.note}”` : ""}
            </div>
          </div>
        )}
        {cancelled && (
          <div
            data-testid="po-confirm-cancelled-banner"
            className="rounded-md p-4 border bg-red-50 border-red-200 text-red-900 flex items-center gap-2 font-semibold"
          >
            <XCircle className="size-5" /> This purchase order has been cancelled.
          </div>
        )}

        <section className="bg-white border border-border rounded-md p-5">
          <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Purchase Order
              </div>
              <div className="text-lg font-bold font-mono" data-testid="po-confirm-number">
                {po.po_number}
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <div>Issued {fmtDate(po.issue_date)}</div>
              {po.expected_date && <div>Requested by {fmtDate(po.expected_date)}</div>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                Issued to
              </div>
              <div className="font-medium">{po.supplier_name}</div>
              <div className="text-muted-foreground">{po.supplier_contact}</div>
            </div>
            <div className="sm:text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                Total
              </div>
              <div className="text-2xl font-bold text-primary font-mono" data-testid="po-confirm-total">
                {fmtMoney(po.total)} {po.currency}
              </div>
              <div className="text-xs text-muted-foreground">
                Subtotal {fmtMoney(po.subtotal)} · Tax {fmtMoney(po.tax)}
              </div>
            </div>
          </div>

          <div className="mt-5 border border-border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Unit cost</th>
                  <th className="text-right px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {(po.lines || []).map((ln, idx) => (
                  <tr key={idx} className="border-t border-border">
                    <td className="px-3 py-2">
                      <div className="font-medium">{ln.name}</div>
                      {ln.specification && (
                        <div className="text-xs text-muted-foreground">{ln.specification}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{ln.qty}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(ln.unit_cost)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(ln.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {po.notes && (
            <div className="mt-4 text-xs text-muted-foreground">
              <span className="font-semibold uppercase tracking-widest">Notes:</span>{" "}
              {po.notes}
            </div>
          )}
        </section>

        {!conf?.confirmed_at && !cancelled && (
          <section className="bg-white border border-border rounded-md p-5" data-testid="po-confirm-form">
            <h2 className="font-bold text-base mb-1">Confirm this order</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Confirm receipt of this purchase order and let KINOS know when
              you expect to deliver. KINOS will be notified immediately.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="overline">Your full name *</Label>
                <Input
                  data-testid="po-confirm-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <Label className="overline">Expected delivery date *</Label>
                <DatePicker
                  testId="po-confirm-date"
                  value={deliveryDate}
                  onChange={setDeliveryDate}
                  placeholder="Pick a delivery date"
                  disabled={{ before: new Date() }}
                />
              </div>
            </div>
            <div className="mb-4">
              <Label className="overline">Note (optional)</Label>
              <Textarea
                data-testid="po-confirm-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Partial shipment arrives earlier…"
              />
            </div>

            <div className="flex justify-end">
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={!name.trim() || !deliveryDate || mut.isPending}
                onClick={() => mut.mutate()}
                data-testid="po-confirm-submit"
              >
                <CheckCircle2 className="size-4 mr-1.5" />
                {mut.isPending ? "Submitting…" : "Confirm receipt & delivery date"}
              </Button>
            </div>
          </section>
        )}

        <footer className="text-center text-xs text-muted-foreground pt-4">
          KINOS Unipessoal LDA · info@kinos.tl · +670 3311480 · Dili, Timor-Leste
        </footer>
      </main>
    </div>
  );
}
